import type { ReactNode } from 'react';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import type { StrategyCoverage, StrategyQueueStatus, StrategyScorecardContext } from './backtestStatusTypes';

interface BacktestStatusBannersProps {
    coverage: StrategyCoverage;
    queueStatus: StrategyQueueStatus | undefined;
    scorecardContext: StrategyScorecardContext | undefined;
}

type BannerTone = 'danger' | 'warning' | 'info';

const TONE_CLASSES: Record<BannerTone, string> = {
    danger: 'border-red-700/60 bg-red-900/20 text-red-300',
    warning: 'border-amber-600/60 bg-amber-900/20 text-amber-200',
    info: 'border-blue-700/60 bg-blue-900/20 text-blue-200'
};

const Banner = ({ tone, children }: { tone: BannerTone; children: ReactNode }) => (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${TONE_CLASSES[tone]}`}>
        {tone === 'info' ? (
            <FaInfoCircle aria-hidden="true" className="mt-0.5 shrink-0" />
        ) : (
            <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
        )}
        <div>{children}</div>
    </div>
);

const BacktestStatusBanners = ({ coverage, queueStatus, scorecardContext }: BacktestStatusBannersProps) => {
    const banners: ReactNode[] = [];

    const hasDrift =
        coverage.backtestVersionKeySummary.currentKeyBacktestsCount === 0 && coverage.backtestVersionKeySummary.otherKeysCount > 0;

    const activeCurrentKeyJobs =
        queueStatus?.strategyVersionKeyMode === 'current' ? queueStatus.byStatus.reduce((sum, row) => sum + row.count, 0) : 0;

    if (hasDrift && activeCurrentKeyJobs > 0) {
        banners.push(
            <Banner key="drift-repopulating" tone="warning">
                Version drift — no backtests for the current strategy settings yet, but repopulation is in progress ({activeCurrentKeyJobs}{' '}
                active queue job{activeCurrentKeyJobs === 1 ? '' : 's'}). This clears once the new backtests land.
            </Banner>
        );
    } else if (hasDrift) {
        banners.push(
            <Banner key="drift" tone="danger">
                Version drift — this strategy has backtests under a different settings version, but none for the current one. Use Populate
                missing (or Reset and repopulate) to regenerate them.
            </Banner>
        );
    }

    if (scorecardContext?.hasBacktests && scorecardContext.hasStaleBacktests) {
        banners.push(
            <Banner key="stale" tone="danger">
                The current scorecard changed after {scorecardContext.staleBacktestCount} backtest
                {scorecardContext.staleBacktestCount === 1 ? '' : 's'} were last refreshed. Repopulate for consistent predicted profit and
                scorecard decisions.
            </Banner>
        );
    }

    const population = coverage.populationProgress;

    if (population.expectedBacktests > 0 && population.remainingBacktests > 0) {
        const progressDetails = [
            population.activeBacktests > 0
                ? `${population.activeBacktests} active backtest${population.activeBacktests === 1 ? '' : 's'}`
                : null,
            population.failedBacktests > 0
                ? `${population.failedBacktests} failed backtest${population.failedBacktests === 1 ? '' : 's'}`
                : null,
            population.unqueuedBacktests > 0 ? `${population.unqueuedBacktests} not yet queued` : null
        ]
            .filter((detail): detail is string => detail !== null)
            .join(', ');

        const needsPopulateAction = population.failedBacktests > 0 || population.unqueuedBacktests > 0;

        banners.push(
            <Banner key="population-incomplete" tone="warning">
                Backtest population is {population.completionPercent ?? 0}% complete ({population.completedBacktests} of{' '}
                {population.expectedBacktests}). {progressDetails}.
                {needsPopulateAction ? ' Use Populate missing to finish or retry them.' : ''}
            </Banner>
        );
    }

    if (banners.length === 0) return null;

    return <div className="mb-4 space-y-2">{banners}</div>;
};

export default BacktestStatusBanners;
